import React, { RefObject, useEffect, useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import CollapsibleList from "components/CollapsibleList";
import CollapsibleSlider from "components/CollapsibleSlider";
import Loader from "components/Loader";
import PaginatedList from "components/PaginatedList";
import ActivityComponentType from "interfaces/ActivityComponentType";
import IColumn from "interfaces/Column";
import IWithId from "interfaces/IWithId";
import { ReactQueryApiQueryHook } from "hooks/api/auto-api-hook-wrapper";
import {
  getChunksFromResponse,
  getTotalResultsFromData
} from "utils/api-utils";
import ListTable from "./ListTable";

interface IRh360Entity {
  id?: string | null;
}

interface IRequest {
  query?: any;
  pagingOptions?: racehorse360.IPagingOptions | null;
  getOptions?: racehorse360.IGetOptions | null;
}

interface IResponse {
  pagingInfo?: racehorse360.IPagingInfo | null;
}

export interface Props<
  E extends IRh360Entity,
  T extends IWithId,
  Req extends IRequest,
  Resp extends IResponse
> {
  entity: E | null;
  apiListHook: ReactQueryApiQueryHook<Req, Partial<Omit<Resp, "toJSON">>>;
  query: any;
  getOptions: racehorse360.IGetOptions;
  listPropName: string;
  tabName: string;
  columns: IColumn[];
  rootRef: RefObject<HTMLElement>;
  component: ActivityComponentType;
  isAlertItemPredicate: (item: T) => boolean;
  commentsSource?: string[];
  onLoad?: (
    items: T[],
    pagingInfo: racehorse360.IPagingInfo,
    alert: boolean
  ) => void;
}

const PAGE_SIZE = 5;

function List<
  E extends IRh360Entity,
  T extends IWithId,
  Req extends IRequest,
  Resp extends IResponse
>(props: Props<E, T, Req, Resp>) {
  const {
    entity,
    apiListHook,
    query,
    getOptions,
    listPropName,
    tabName,
    columns,
    rootRef,
    component,
    isAlertItemPredicate,
    commentsSource,
    onLoad
  } = props;

  const [activeCount, setActiveCount] = useState<number>(null);
  const [pages, setPages] = useState<T[][]>([]);

  const totalRequest: IRequest = {
    query: query,
    pagingOptions: {
      maxResults: 1,
      includeSummary: true
    }
  };

  if (!apiListHook) {
    return <Loader />;
  }

  const { isFetching: isTotalFetching, data: totalData } = apiListHook(
    `${entity.id}-total`,
    totalRequest as Req,
    {
      enabled: Boolean(entity)
    }
  );
  const totalResults = getTotalResultsFromData(totalData);

  const fullRequest: IRequest = {
    query: query,
    pagingOptions: {
      maxResults: totalResults,
      offset: 0,
      includeSummary: true
    },
    getOptions: getOptions
  };

  const { isFetching: isFullFetching, data: fullData } = apiListHook(
    `${entity.id}-full`,
    fullRequest as Req,
    {
      enabled: Boolean(totalResults),
      onSuccess: data => {
        const chunks = getChunksFromResponse<T>(data[listPropName]);
        const activeListsCount = data[listPropName].filter((item: T) =>
          isAlertItemPredicate(item)
        ).length;
        setPages(chunks);
        setActiveCount(activeListsCount);
        onLoad?.(
          data[listPropName],
          data.pagingInfo,
          Boolean(activeListsCount)
        );
      }
    }
  );

  const isFetching = isTotalFetching || isFullFetching;

  const isSliderView = component === ActivityComponentType.SLIDER;

  useEffect(() => {
    if (fullData && fullData[listPropName] && onLoad) {
      onLoad(fullData[listPropName], fullData?.pagingInfo, false);
    }
  }, []);

  if (isFetching && !isSliderView) {
    return <Loader />;
  }

  const renderTable = (items = []) => (
    <ListTable
      tabName={tabName}
      items={items}
      columns={columns}
      isAlertItemPredicate={isAlertItemPredicate}
      commentsSource={commentsSource}
    />
  );

  const renderRequredComponent = () => {
    switch (component) {
      case ActivityComponentType.SLIDER:
        return (
          <CollapsibleSlider<T>
            title={tabName}
            rootRef={rootRef}
            slides={pages}
            isFetching={isFetching}
            totalCount={totalResults}
            activeCount={activeCount}
            renderSlide={renderTable}
            slideSize={PAGE_SIZE}
          />
        );
      case ActivityComponentType.PAGINATED:
        return (
          <PaginatedList<T>
            pageSize={PAGE_SIZE}
            pages={pages}
            isFetching={isFetching}
            renderPage={renderTable}
            totalCount={totalResults}
            noContentTitle={`No ${tabName}}`}
          />
        );
      case ActivityComponentType.LIST:
        return (
          <CollapsibleList<T>
            title={tabName}
            items={fullData?.[listPropName]}
            rootRef={rootRef}
            isFetching={isFetching}
            totalResults={totalResults}
            activeCount={activeCount}
          >
            {renderTable(fullData?.[listPropName])}
          </CollapsibleList>
        );
    }
  };

  return renderRequredComponent();
}

export default List;
